import {
  Rrule_Day,
  Rrule_Frequency,
  type RRuleInput,
  type RRuleByDayInput,
} from "@/graphql/generated-types";
import { formatDateTimeWithTimezone } from "@/stories/utils/dates";
import type { RRuleState } from "@/types";

// Map weekday index to GraphQL Rrule_Day enum
const WEEKDAY_TO_RRULE_DAY: Record<number, Rrule_Day> = {
  0: Rrule_Day.Mo,
  1: Rrule_Day.Tu,
  2: Rrule_Day.We,
  3: Rrule_Day.Th,
  4: Rrule_Day.Fr,
  5: Rrule_Day.Sa,
  6: Rrule_Day.Su,
};

// Map cadence to GraphQL Rrule_Frequency enum
const CADENCE_TO_FREQ: Record<number, Rrule_Frequency> = {
  1: Rrule_Frequency.Daily,
  7: Rrule_Frequency.Weekly,
  30: Rrule_Frequency.Monthly,
  365: Rrule_Frequency.Yearly,
};

export default (state: RRuleState): RRuleInput => {
  const result: RRuleInput = {};

  // Set frequency
  const freq = CADENCE_TO_FREQ[state.cadence];
  if (freq) {
    result.FREQ = freq;
  }

  // Set interval (occurrence)
  result.INTERVAL = state.occurence;

  // Handle BYDAY for weekly recurrence
  if (state.cadence === 7 && state.days) {
    const byDay: RRuleByDayInput[] = [];

    Object.keys(state.days).forEach((dayKey) => {
      const dayIndex = parseInt(dayKey);
      if (state.days[dayIndex] && WEEKDAY_TO_RRULE_DAY[dayIndex]) {
        byDay.push({
          day: WEEKDAY_TO_RRULE_DAY[dayIndex],
        });
      }
    });

    if (byDay.length > 0) {
      result.BYDAY = byDay;
    }
  }

  // Handle BYDAY for monthly recurrence with day selection
  if (
    state.cadence === 30 &&
    state.daySelection !== undefined &&
    state.dayOrder !== undefined
  ) {
    const day = WEEKDAY_TO_RRULE_DAY[state.daySelection];
    if (day) {
      // With the BYSETPOS strategy the position comes from BYSETPOS, so BYDAY
      // must be the plain weekday. Sending both (e.g. 3WE + BYSETPOS=3) asks
      // for the 3rd item of a single-item set, which never matches.
      result.BYDAY = [
        state.useBysetpos ? { day } : { day, occurrence: state.dayOrder },
      ];
    }
  }

  // Set WKST for monthly cadence
  if (state.cadence === 30 && state.daySelection !== undefined) {
    result.WKST = WEEKDAY_TO_RRULE_DAY[state.daySelection];
  }

  // Set BYSETPOS for monthly settings when strategy is enabled
  if (
    state.useBysetpos &&
    state.cadence === 30 &&
    state.dayOrder !== undefined
  ) {
    result.BYSETPOS = [state.dayOrder];
  }

  // Set start date (DTSTART)
  if (state.startDate) {
    // Convert Unix timestamp to ISO date string with timezone offset
    const startDate = new Date(state.startDate * 1000);
    result.DTSTART = formatDateTimeWithTimezone(startDate);
  }

  // Handle ending conditions
  const { endingType, endingDate } = state;

  if (endingType === undefined || endingType === "finish_after") {
    // Use COUNT
    result.COUNT = state.repeatAmount;
  } else if (endingType === "finish_on" && endingDate) {
    // Use UNTIL - format with timezone offset
    const endDate = new Date(endingDate * 1000);
    result.UNTIL = formatDateTimeWithTimezone(endDate);
  }
  // For "no_end_date", neither COUNT nor UNTIL is set

  return result;
};
